import React, { useEffect, useRef } from 'react';

const AnimatedBackground = ({ interactive = true, density = 'normal' }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: null, y: null });
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });

    // Performance: Set canvas size to viewport only (not entire page)
    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2); // Cap at 2x for performance
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
      initParticles();
    };

    // Throttled mouse tracking
    let lastMouseUpdate = 0;
    const handleMouseMove = (e) => {
      const now = Date.now();
      if (now - lastMouseUpdate < 16) return; // ~60fps throttle
      lastMouseUpdate = now;
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouseRef.current.x = null;
      mouseRef.current.y = null;
    };

    // Simple particle - no classes for better performance
    const createParticle = (width, height) => {
      const colors = [
        [96, 165, 250],   // blue
        [167, 139, 250],  // purple
        [34, 211, 238],   // cyan
        [129, 140, 248],  // indigo
      ];
      const color = colors[Math.floor(Math.random() * colors.length)];

      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 1.5 + 1,
        opacity: Math.random() * 0.5 + 0.3,
        color,
      };
    };

    // Performance: Much fewer particles (80-200 instead of 2000-5000)
    const getParticleCount = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile) return 40; // Very few on mobile

      switch (density) {
        case 'high': return 150;
        case 'normal': return 100;
        case 'low': return 60;
        default: return 100;
      }
    };

    const initParticles = () => {
      const count = getParticleCount();
      const width = window.innerWidth;
      const height = window.innerHeight;
      particlesRef.current = Array.from({ length: count }, () => createParticle(width, height));
    };

    // Optimized animation loop
    const animate = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      ctx.clearRect(0, 0, width, height);

      const particles = particlesRef.current;
      const mouse = mouseRef.current;
      const connectionDistance = 120;
      const connectionDistanceSq = connectionDistance * connectionDistance;

      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Mouse interaction (simplified)
        if (interactive && mouse.x !== null) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const distSq = dx * dx + dy * dy;

          if (distSq < 22500) { // 150px radius
            const dist = Math.sqrt(distSq);
            const force = (150 - dist) / 150;
            const angle = Math.atan2(dy, dx);
            p.x += Math.cos(angle) * force * 2;
            p.y += Math.sin(angle) * force * 2;
          }
        }

        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;

        // Draw particle (simple circle, no gradient)
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color[0]}, ${p.color[1]}, ${p.color[2]}, ${p.opacity})`;
        ctx.fill();
      }

      // Draw connections (limited)
      ctx.lineWidth = 0.5;
      let connections = 0;
      const maxConnections = 150; // Much lower limit

      for (let i = 0; i < particles.length && connections < maxConnections; i++) {
        const p1 = particles[i];

        for (let j = i + 1; j < particles.length && connections < maxConnections; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;

          // Quick bounds check before expensive sqrt
          if (Math.abs(dx) > connectionDistance || Math.abs(dy) > connectionDistance) continue;

          const distSq = dx * dx + dy * dy;
          if (distSq > connectionDistanceSq) continue;

          const opacity = (1 - Math.sqrt(distSq) / connectionDistance) * 0.2;

          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.strokeStyle = `rgba(${p1.color[0]}, ${p1.color[1]}, ${p1.color[2]}, ${opacity})`;
          ctx.stroke();
          connections++;
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Initialize
    resizeCanvas();

    // Debounced resize handler
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeCanvas, 200);
    };

    window.addEventListener('resize', handleResize);
    if (interactive) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      window.addEventListener('mouseleave', handleMouseLeave);
    }

    // Start animation
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (interactive) {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseleave', handleMouseLeave);
      }
      clearTimeout(resizeTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [interactive, density]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

export default AnimatedBackground;
